import React, { useEffect, useState } from 'react';
import { AutoComplete, Modal, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import { ReviewPoolItem } from '../types/reviewPool';
import { standardizeFailContentKey, useAddressStandardize } from '../hooks/useAddressStandardize';
import { useLocationCascade } from '../hooks/useLocationCascade';
import {
  FIND_COOPERATION_WAREHOUSES_BY_ADDRESS,
  FindCooperationWarehousesByAddressResponse,
  FindCooperationWarehousesByAddressVariables,
  SameAddressSite,
} from '../graphql/queries/findCooperationWarehousesByAddress';
import { SiteAddressForSave } from './PickupSiteFormDrawer';
import { MODAL_HEADER_STYLE, MODAL_FOOTER_STYLE, modalBodyStyle } from '../utils/modalChrome';

interface Props {
  open: boolean;
  item: ReviewPoolItem | null;
  onClose: () => void;
  /**
   * 补录完成 → 带着实际上门地址(B)进 AB 映射向导。
   *
   * <p>**不落库**：条目地址必须保持为原始下单地址(A)。AB 映射 / 合并到已有写出的
   * `ab_mapping.address_core_hash` 与 `relate.address_hash` 都取 `pool.addressCoreHash`，
   * 一旦把条目地址改成 B，这些键就变成 B 的 hash，而订单永远填 A —— 映射写下去就是死的。
   */
  onNext?: (actualAddress: SiteAddressForSave) => void;
}

interface AddrFields {
  /** 街道（**不含** dock） */
  addressLine: string;
  /** Dock / 小号，选填。往下传时拼进 addressLine1 —— 这正是 core_hash 的口径（标准化 + dock），与站点表单一致 */
  dock: string;
  city: string;
  state: string;
  zipcode: string;
}

/**
 * 补录实际上门地址（标准化失败卡片）：只读下单原始地址(A) + 可编辑实际上门地址(B)，
 * 标准化通过后「下一步」进 AB 映射向导。
 *
 * <p><b>本弹窗不写任何库</b>。它曾经调 `updateReviewPoolAddress` 用 B 覆盖条目地址，那是错的：
 * AB 映射（`ab_mapping.address_core_hash`）与合并到已有（`relate.address_hash`）的命中键都取自
 * 条目的 `addressCoreHash`，一旦覆盖成 B 的 hash，而订单永远填 A，映射从写下去就命中不了。
 * 条目地址 = 下单事实，B 是另一个地址，两者不是同一条记录。
 */
const ModifyAddressModal: React.FC<Props> = ({ open, item, onClose, onNext }) => {
  const { t } = useTranslation();
  const [addr, setAddr] = useState<AddrFields>({ addressLine: '', dock: '', city: '', state: '', zipcode: '' });
  const [standardized, setStandardized] = useState(false);
  const [failed, setFailed] = useState(false);
  /** 标准化失败原因：决定提示是「可重试」还是「必须改地址」 */
  const [failReason, setFailReason] = useState<string | null>(null);
  /** 标准化成功后按 core_hash 精确查到的同址站点（B 候选）；null=尚未查 */
  const [sameSites, setSameSites] = useState<SameAddressSite[] | null>(null);
  const { standardize, loading: stdLoading } = useAddressStandardize();
  const [findSameAddressSites] = useLazyQuery<
    FindCooperationWarehousesByAddressResponse,
    FindCooperationWarehousesByAddressVariables
  >(FIND_COOPERATION_WAREHOUSES_BY_ADDRESS, { fetchPolicy: 'network-only' });

  useEffect(() => {
    if (open) {
      setAddr({ addressLine: '', dock: '', city: '', state: '', zipcode: '' });
      setStandardized(false);
      setFailed(false);
      setSameSites(null);
    }
  }, [open]);

  const mp = (k: string) => t(`pickup.addressReviewPool.modifyAddress.${k}`) as string;
  const wt = (k: string) => t(`pickup.addressReviewPool.siteForm.${k}`) as string;
  // dock 选填，不进必填校验
  const allFilled = !!(addr.addressLine && addr.city && addr.state && addr.zipcode);
  const { stateOptions, cityOptions, zipcodeOptions, isCustom, filterOption } = useLocationCascade(
    addr.state,
    addr.city,
  );
  const hasCustom =
    isCustom(addr.state, stateOptions) || isCustom(addr.city, cityOptions) || isCustom(addr.zipcode, zipcodeOptions);

  // 真实标准化：调地址库校验实际上门地址；成功回填 normalized 四段 + 置已标准化，失败置 failed
  const handleStandardize = async () => {
    if (!allFilled) {
      message.warning(mp('failTip'));
      return;
    }
    try {
      // 传不含 dock 的街道：地址库对小号解析弱，含 dock 会干扰大号识别（与站点表单、后端 parseDockInfo 同思路）
      const res = await standardize({ street: addr.addressLine, city: addr.city, state: addr.state, zip: addr.zipcode });
      if (res.isValid && res.normalized) {
        const dock = addr.dock?.trim() || res.dock || '';
        setAddr({
          addressLine: res.normalized.addressLine1,
          // 运营把小号误填进街道框时 hook 会抓出来；Dock 框为空才回填，已填以运营输入为准
          dock,
          city: res.normalized.city,
          state: res.normalized.state,
          zipcode: res.normalized.zipcode,
        });
        setStandardized(true);
        setFailed(false);
        setFailReason(null);
        // 查同址站点：走 core_hash 精确口径（与建站/命中同一套），命中即为可直接映射的 B。
        // 查不到不是错误——AB 向导 Step2 支持「无匹配 → 新建 B」，两种情况都能往下走。
        if (item) {
          try {
            const { data } = await findSameAddressSites({
              variables: {
                warehouseId: item.warehouseId,
                // 带 dock：core_hash 口径含 dock，去掉会查成另一个地址
                addressLine1: [res.normalized.addressLine1, dock].filter(Boolean).join(' '),
                city: res.normalized.city,
                state: res.normalized.state,
                zipcode: res.normalized.zipcode,
              },
            });
            setSameSites(data?.findCooperationWarehousesByAddress ?? []);
          } catch {
            // 查站点失败不阻断补录：置空数组按「无同址站点」走，Step2 仍可新建 B
            setSameSites([]);
          }
        }
      } else {
        setStandardized(false);
        setFailed(true);
        setFailReason(res.fallbackReason ?? null);
      }
    } catch {
      setStandardized(false);
      setFailed(true);
      // 请求本身异常（网络/鉴权），与地址库兜底原因无关 → 走通用文案
      setFailReason(null);
    }
  };

  /**
   * 下一步：把补录的实际上门地址(B)交给 AB 映射向导，**不写库**。
   *
   * 这里刻意不再调 `updateReviewPoolAddress`：那会用 B 覆盖条目地址，而 AB 映射与合并到已有
   * 写出的命中键都取自条目（`pool.addressCoreHash`）。覆盖后键变成 B 的 hash，订单却永远填 A，
   * 映射自写下去就命中不了。条目地址是下单事实，不可改写。
   */
  const handleNext = () => {
    if (!standardized || !item) return;
    onNext?.({
      // dock 拼回 addressLine1：core_hash 口径含 dock，向导据此查/建 B 站点
      addressLine1: [addr.addressLine, addr.dock?.trim()].filter(Boolean).join(' '),
      city: addr.city,
      state: addr.state,
      zipcode: addr.zipcode,
      country: 'US',
    });
    onClose();
  };

  const colLabel: React.CSSProperties = { fontSize: 12, color: '#6b6778', textAlign: 'center', marginBottom: 6 };
  /**
   * 五列统一行高。原生 input 不写死高度时高度 = 内容行高 + padding，会比 antd Select 的
   * controlHeight(32) 高出几像素；本行用 align-items:flex-end 底对齐，高度不一致会让
   * 「地址/Dock」和「州/城市/邮编」的标签错位一截。故显式统一，改这里要连带改下方 style 里的 Select 覆盖。
   */
  const FIELD_H = 34;
  const roInput: React.CSSProperties = {
    width: '100%',
    height: FIELD_H,
    boxSizing: 'border-box',
    border: '1px solid #ececf2',
    borderRadius: 7,
    padding: '0 10px',
    fontSize: 12.5,
    background: '#f7f6fa',
    color: '#4a4658',
    textAlign: 'center',
    outline: 'none',
  };
  const editInput: React.CSSProperties = {
    width: '100%',
    height: FIELD_H,
    boxSizing: 'border-box',
    border: '1px solid #d9d5e6',
    borderRadius: 7,
    padding: '0 10px',
    fontSize: 12.5,
    textAlign: 'center',
    outline: 'none',
  };

  /**
   * 列标题。required 时前置红星，口径与 allFilled 一致：地址/州/城市/邮编必填，Dock 选填。
   * 只用于「实际上门揽收地址」那排——上排是下单原值只读展示，标必填没有意义。
   */
  const labelNode = (label: string, required?: boolean) => (
    <div style={colLabel}>
      {required && <span style={{ color: '#ff4d4f', marginRight: 3 }}>*</span>}
      {label}
    </div>
  );

  const origCol = (label: string, value?: string) => (
    <div>
      <div style={colLabel}>{label}</div>
      <input readOnly value={value || ''} style={roInput} />
    </div>
  );
  const editCol = (label: string, key: keyof AddrFields, flex: number, required?: boolean) => (
    <div style={{ flex }}>
      {labelNode(label, required)}
      <input
        value={addr[key]}
        placeholder={t('common.pleaseInput', '请输入') as string}
        onChange={(e) => {
          const v = e.target.value;
          setAddr((p) => ({ ...p, [key]: v }));
          setStandardized(false);
          setFailed(false);
          setSameSites(null);
        }}
        style={editInput}
      />
    </div>
  );

  /** 联动下拉列：与 editCol 同一 flex 布局，保持上下 5 列逐列对照；控件换 AutoComplete 以允许自定义输入 */
  const cascadeCol = (
    label: string,
    key: 'city' | 'state' | 'zipcode',
    flex: number,
    options: { label: string; value: string }[],
  ) => (
    <div style={{ flex }} className="modify-addr-cascade">
      {labelNode(label, true)}
      <AutoComplete
        value={addr[key]}
        placeholder={t('common.pleaseInput', '请输入') as string}
        options={options}
        filterOption={filterOption}
        style={{ width: '100%' }}
        onChange={(val) => {
          // 改州清空城市+邮编、改城市清空邮编：下级选项整批换掉，留旧值会出现不存在的组合
          setAddr((p) => ({
            ...p,
            [key]: val,
            ...(key === 'state' ? { city: '', zipcode: '' } : {}),
            ...(key === 'city' ? { zipcode: '' } : {}),
          }));
          setStandardized(false);
          setFailed(false);
          setSameSites(null);
        }}
      />
    </div>
  );

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
      <button
        onClick={onClose}
        style={{
          border: '1px solid #b7b2c4',
          background: '#8a8698',
          color: '#fff',
          fontSize: 13,
          fontWeight: 700,
          padding: '9px 24px',
          borderRadius: 9,
          cursor: 'pointer',
        }}
      >
        {t('common.cancel', '取消') as string}
      </button>
      <button
        onClick={handleNext}
        disabled={!standardized}
        style={{
          border: 'none',
          background: standardized ? '#6d28d9' : '#c9bff0',
          color: '#fff',
          fontSize: 13,
          fontWeight: 700,
          padding: '9px 26px',
          borderRadius: 9,
          cursor: standardized ? 'pointer' : 'not-allowed',
        }}
      >
        {mp('next')}
      </button>
    </div>
  );

  return (
    <Modal
      open={open}
      width={820}
      onCancel={onClose}
      footer={footer}
      // header/footer 固定并各带分隔线，滚动只发生在两线之间（对齐 PRD 原型）
      styles={{
        header: MODAL_HEADER_STYLE,
        body: modalBodyStyle('70vh'),
        footer: MODAL_FOOTER_STYLE,
      }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 5, height: 22, background: '#7c4bf0', borderRadius: 3, display: 'inline-block' }} />
          <span style={{ fontSize: 18, fontWeight: 800 }}>{mp('title')}</span>
        </div>
      }
    >
      {/* AutoComplete 视觉对齐同排的原生 input（居中、同圆角/边框），否则 5 列高低不齐 */}
      <style>{`.modify-addr-cascade .ant-select-single { height: ${FIELD_H}px; }
        .modify-addr-cascade .ant-select-selector { height: ${FIELD_H}px !important; border-radius: 7px !important; border-color: #d9d5e6 !important; }
        .modify-addr-cascade .ant-select-selection-search-input { height: ${FIELD_H - 2}px !important; }
        .modify-addr-cascade .ant-select-selection-item, .modify-addr-cascade .ant-select-selection-placeholder { line-height: ${FIELD_H - 2}px !important; }
        .modify-addr-cascade .ant-select-selection-search-input, .modify-addr-cascade .ant-select-selection-placeholder { text-align: center; font-size: 12.5px; }`}</style>

      {/* 下单原始地址信息（只读，5 列横排） */}
      <div style={{ fontSize: 13.5, fontWeight: 800, color: '#3a3550', marginBottom: 14 }}>{mp('orderAddress')}</div>
      {/* 5 列与下排逐列对照（新增 Dock 列），运营能一眼看出改了哪一段。
          列序「州 → 城市 → 邮编」与站点表单 PickupAddressFields 保持一致（州是城市的上级，先大后小） */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 0.8fr 0.8fr 1fr 1fr', gap: 14, marginBottom: 24 }}>
        {origCol(mp('addressLine'), item?.addressLine1 ?? item?.displayAddress)}
        {origCol(wt('dock'), item?.dock)}
        {origCol(mp('state'), item?.state)}
        {origCol(mp('city'), item?.city)}
        {origCol(mp('zipcode'), item?.zipcode)}
      </div>

      {/* 实际上门揽收地址（可编辑，4 列 + 标准化按钮同行） */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <span style={{ fontSize: 13.5, fontWeight: 800, color: '#3a3550' }}>{mp('actualAddress')}</span>
        <span style={{ fontSize: 11.5, color: '#6d28d9' }}>{mp('googleHint')}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginBottom: 14 }}>
        {editCol(mp('addressLine'), 'addressLine', 2, true)}
        {/* Dock 选填：allFilled 不校验它，故不标红星 */}
        {editCol(wt('dock'), 'dock', 0.8)}
        {cascadeCol(mp('state'), 'state', 0.8, stateOptions)}
        {cascadeCol(mp('city'), 'city', 1, cityOptions)}
        {cascadeCol(mp('zipcode'), 'zipcode', 1, zipcodeOptions)}
        <button
          onClick={handleStandardize}
          disabled={stdLoading}
          style={{
            border: 'none',
            background: '#7c4bf0',
            color: '#fff',
            fontSize: 12.5,
            fontWeight: 700,
            height: FIELD_H,
            padding: '0 18px',
            borderRadius: 8,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {mp('standardize')}
        </button>
      </div>

      {standardized && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            background: '#f2fbf7',
            border: '1px solid #cdeee6',
            borderRadius: 8,
            padding: '10px 14px',
          }}
        >
          <span style={{ fontSize: 11.5, fontWeight: 800, color: '#0f766e' }}>✓ {mp('standardizeOk')}</span>
          {/* 告诉运营点「下一步」会发生什么：有同址站点=直接映射过去；没有=进向导新建 B 再映射。
              两条路都不会改动本条目的下单地址 —— 那是 AB 命中键的来源。 */}
          {sameSites !== null && (
            <span style={{ fontSize: 11.5, color: '#3f6f66' }}>
              {sameSites.length > 0
                ? `${mp('existingSiteHint')}${sameSites
                    .map((s) => `${s.name || ''}(ID ${s.id})`)
                    .join('、')}`
                : mp('noExistingSiteHint')}
            </span>
          )}
        </div>
      )}
      {hasCustom && !standardized && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#d97706', marginTop: 4 }}>
          <span>⚠️</span>
          <span>{wt('customValueHint')}</span>
        </div>
      )}
      {failed && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#e11d48', marginTop: 4 }}>
          <span>⚠️</span>
          <span>{wt(standardizeFailContentKey(failReason))}</span>
        </div>
      )}
    </Modal>
  );
};

export default ModifyAddressModal;
